import { useEffect, useState } from 'react';
import { useTheme } from '@mui/material';
import { NavLink } from 'react-router-dom';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import PhotoLibraryIcon from '@mui/icons-material/PhotoLibrary';
import AutoStoriesIcon from '@mui/icons-material/AutoStories';
import Social from '../social/social';
import {
  ADDRESS_MAPS_URL,
  Eyebrow,
  SectionHeading,
  IconRow,
  LanguageSwitcher,
  MenuFooterBar,
} from '../menuShared';
import { useAppDispatch, useAppSelector } from '../../app/hooks';
import { selectLanguage, updateLanguage } from '../../app/slices/languageSlice';
import {
  EN,
  RO,
  street,
  city,
  state,
  zip,
  homePath,
  homeString,
  servicePath,
  servicesString,
  aboutPath,
  aboutString,
  givingPath,
  donationsString,
  contactPath,
  contactString,
  secondaryConnectEyebrowString,
  secondaryConnectHeadingString,
  announcementsString,
  announcementsPath,
  eventsPath,
  eventsPageHeadingString,
  bibleReadingPlanString,
  bibleReadingPlanPath,
  mediaString,
  mediaPath,
  ministriesString,
  awanaPath,
  ezraPath,
} from '../../constants';

const PRIMARY_NAV = [
  { to: homePath, string: homeString },
  { to: servicePath, string: servicesString },
  { to: givingPath, string: donationsString, external: true },
  { to: aboutPath, string: aboutString },
  { to: contactPath, string: contactString },
  { to: announcementsPath, string: announcementsString },
  { to: eventsPath, string: eventsPageHeadingString },
];

export function MobileMenu(props) {
  const theme = useTheme();
  const language = useAppSelector(selectLanguage);
  const dispatch = useAppDispatch();
  const [ministriesOpen, setMinistriesOpen] = useState(false);

  useEffect(() => {
    document.body.style.overflow = props.isActive ? 'hidden' : 'unset';
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [props.isActive]);

  if (!props.isActive) {
    return <></>;
  }

  const exploreLinkStyle = {
    fontFamily: theme.typography.paragraphFontFamily,
    fontSize: '16px',
    fontWeight: 500,
    color: theme.palette.text.primary,
    textDecoration: 'none',
  };

  const navItemStyle = {
    display: 'block',
    padding: '14px 2px',
    borderBottom: `1px solid ${theme.palette.brand.border}`,
    fontFamily: theme.typography.fontFamily,
    fontSize: '19px',
    fontWeight: 600,
    textDecoration: 'none',
  };

  return (
    <div
      style={{
        position: 'fixed',
        top: 'var(--nav-height)',
        left: 0,
        width: '100%',
        height: 'calc(100dvh - var(--nav-height))',
        zIndex: '101',
        backgroundColor: theme.palette.background.default,
        overflowY: 'auto',
        WebkitOverflowScrolling: 'touch',
        overscrollBehavior: 'contain',
        fontFamily: theme.typography.fontFamily,
      }}
    >
      <div style={{ padding: '8px 24px 40px', boxSizing: 'border-box' }}>
        {/* PAGES - Home through Announcements, in one flat list. The
            Ministries dropdown lives inline here (under Announcements)
            rather than in its own "Explore" section like before. */}
        <div style={{ marginBottom: '20px' }}>
          {PRIMARY_NAV.map((item) => {
            if (item.external) {
              return (
                <a
                  key={item.to}
                  href={item.to}
                  onClick={props.handleClick}
                  target="_blank"
                  rel="noreferrer"
                  style={{ ...navItemStyle, color: theme.palette.primary.dark }}
                >
                  {item.string(language)}
                </a>
              );
            }

            return (
              <NavLink
                key={item.to}
                to={item.to}
                onClick={props.handleClick}
                style={({ isActive }) => ({
                  ...navItemStyle,
                  color: isActive ? theme.palette.primary.main : theme.palette.primary.dark,
                })}
              >
                {item.string(language)}
              </NavLink>
            );
          })}

          <button
            type="button"
            onClick={() => setMinistriesOpen(!ministriesOpen)}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              width: '100%',
              background: 'none',
              border: 'none',
              padding: '14px 2px',
              margin: 0,
              cursor: 'pointer',
              textAlign: 'left',
              borderBottom: ministriesOpen ? 'none' : `1px solid ${theme.palette.brand.border}`,
            }}
          >
            <span style={{ ...navItemStyle, padding: 0, border: 'none', color: theme.palette.primary.dark }}>
              {ministriesString(language)}
            </span>
            <ExpandMoreIcon
              sx={{
                fontSize: '22px',
                color: theme.palette.text.secondary,
                transform: ministriesOpen ? 'rotate(180deg)' : 'none',
                transition: 'transform 0.2s',
              }}
            />
          </button>

          {ministriesOpen && (
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '10px',
                padding: '12px 0 14px 4px',
                borderBottom: `1px solid ${theme.palette.brand.border}`,
              }}
            >
              <NavLink to={awanaPath} onClick={props.handleClick} style={exploreLinkStyle}>
                Awana
              </NavLink>
              <NavLink to={ezraPath} onClick={props.handleClick} style={exploreLinkStyle}>
                Ezra Conference
              </NavLink>
            </div>
          )}
        </div>

        {/* BIBLE READING PLAN + PHOTOS - deliberately understated: same
            weight as the address link in Connect below rather than bold
            primary-nav rows, but each still its own line with an icon so
            neither disappears. */}
        <div style={{ marginBottom: '20px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
          <NavLink
            to={bibleReadingPlanPath}
            onClick={props.handleClick}
            style={{ ...exploreLinkStyle, display: 'flex', alignItems: 'center', gap: '10px' }}
          >
            <AutoStoriesIcon sx={{ fontSize: '18px', color: theme.palette.primary.dark }} />
            {bibleReadingPlanString(language)}
          </NavLink>

          <NavLink
            to={mediaPath}
            onClick={props.handleClick}
            style={{ ...exploreLinkStyle, display: 'flex', alignItems: 'center', gap: '10px' }}
          >
            <PhotoLibraryIcon sx={{ fontSize: '18px', color: theme.palette.primary.dark }} />
            {mediaString(language)}
          </NavLink>
        </div>

        {/* CONNECT - social icons and the address read as one compact
            block instead of separately-spaced sections. */}
        <div style={{ marginTop: '24px' }}>
          <Eyebrow theme={theme}>{secondaryConnectEyebrowString(language)}</Eyebrow>
          <SectionHeading theme={theme}>{secondaryConnectHeadingString(language)}</SectionHeading>

          <div style={{ marginBottom: '12px' }}>
            <Social />
          </div>

          <IconRow icon={<LocationOnIcon sx={{ fontSize: '20px' }} />} theme={theme} flat>
            <a
              href={ADDRESS_MAPS_URL}
              target="_blank"
              rel="noreferrer"
              style={{
                fontFamily: theme.typography.paragraphFontFamily,
                fontSize: '15px',
                color: theme.palette.text.primary,
                textDecoration: 'none',
              }}
            >
              {street}, {city}, {state} {zip}
            </a>
          </IconRow>
        </div>

        <MenuFooterBar
          theme={theme}
          language={language}
          handleClick={props.handleClick}
          trailing={
            <LanguageSwitcher
              theme={theme}
              language={language}
              onChangeLanguage={(lang) => dispatch(updateLanguage(lang))}
              EN={EN}
              RO={RO}
              size={16}
            />
          }
          style={{
            borderTop: `1px solid ${theme.palette.brand.border}`,
            marginTop: '32px',
            paddingTop: '20px',
          }}
        />
      </div>
    </div>
  );
}
